import { useState, useEffect, useRef } from 'react'
import {
  SimpleForm,
  TextInput,
  SelectInput,
  ImageInput,
  ReferenceArrayInput,
  AutocompleteArrayInput,
  ArrayInput,
  SimpleFormIterator,
  ReferenceInput,
  minLength,
  maxLength,
  FormDataConsumer,
  AutocompleteInput,
  BooleanInput,
  NumberInput,
  minValue,
  maxValue,
  usePermissions,
  useGetOne,
  useGetMany,
} from 'react-admin'
import { Tabs, Tab, Box } from '@mui/material'
import Grid from '@mui/material/Grid'
import { useWatch, useFormContext } from 'react-hook-form'

import CustomImageField from '../../components/CustomImageField'
import BookToolbar from '../../components/BookToolbar'
import GridContainer from '../../components/GridContainer'
import CustomFileField from '../../components/CustomFileField'
import { validateRequired } from '../../utils/validate'
import CustomFileInput from '../../components/CustomFileInput'
import CustomAudioPlayerField from '../../components/CustomAudioPlayerField'
import { ROLES } from '../../components/constants'
import PeriodCommissionsInput from '../../components/PeriodCommissionsInput'

import { PRICE_CHOICES } from './priceTiers'
import ChaptersManagerInput from './ChaptersManagerInput'

const MIN_AGE_CHOICES = [
  { id: 3, name: '3+' },
  { id: 6, name: '6+' },
  { id: 11, name: '11+' },
]

const PublisherCommission = () => {
  const { permissions } = usePermissions()
  const validateCommission = [minValue(0), maxValue(100)]

  const publisherId = useWatch({
    name: 'publisherId',
  })

  const { data: user, isLoading } = useGetOne(
    'users',
    { id: publisherId },
    {
      enabled: !!publisherId,
    },
  )

  return (
    (permissions === ROLES.ADMIN || permissions === ROLES.QC) && (
      <>
        <Grid item xs={4}>
          <FormDataConsumer>
            {({ formData }) => {
              if (!formData || isLoading) return null
              return (
                <>
                  <NumberInput
                    label={`Commission (0-100) / default: ${
                      user?.defaultCommission
                    }%`}
                    source="commission"
                    fullWidth
                    // defaultValue={user?.defaultCommission}
                    validate={validateCommission}
                    variant="standard"
                    classes={{ root: 'number-input-without-arrows' }}
                  />
                  <NumberInput
                    label={`Included Lib Commission (0-100) / default: ${
                      user?.includedLibCommission
                    }%`}
                    source="includedLibCommission"
                    // defaultValue={user?.includedLibCommission}
                    fullWidth
                    validate={validateCommission}
                    variant="standard"
                    classes={{ root: 'number-input-without-arrows' }}
                  />
                  <NumberInput
                    fullWidth
                    variant="standard"
                    label={`% Platform fee (0-100) / default: ${user?.platformFeePercentage || 0}%`}
                    // defaultValue={user?.platformFeePercentage}
                    source="platformFeePercentage"
                    margin="normal"
                    validate={validateCommission}
                    classes={{ root: 'number-input-without-arrows' }}
                  />
                  {/*<NumberInput*/}
                  {/*  fullWidth*/}
                  {/*  variant="standard"*/}
                  {/*  label={`% Tax fee (0-100) / default: ${user?.taxFeePercentage || 0}`}*/}
                  {/*  source="taxFeePercentage"*/}
                  {/*  margin="normal"*/}
                  {/*  defaultValue={user?.taxFeePercentage || 0}*/}
                  {/*  validate={[minValue(0), maxValue(100)]}*/}
                  {/*  classes={{ root: 'number-input-without-arrows' }}*/}
                  {/*/>*/}
                </>
              )
            }}
          </FormDataConsumer>
        </Grid>

        <Grid item xs={12} ml={2}>
          <PeriodCommissionsInput
            fullWidth
            variant="standard"
            label="Period commissions"
            source="commissions"
          />
        </Grid>
      </>
    )
  )
}

const ForeignPublisherCommission = () => {
  const { permissions } = usePermissions()
  const validateCommission = [minValue(0), maxValue(100)]

  const publisherId = useWatch({
    name: 'foreignPublisherId',
  })

  const { data: user, isLoading } = useGetOne(
    'users',
    { id: publisherId },
    {
      enabled: !!publisherId,
    },
  )

  return (
    (permissions === ROLES.ADMIN || permissions === ROLES.QC) && (
      <>
        <Grid item xs={4}>
          <FormDataConsumer>
            {({ formData }) => {
              if (!formData || isLoading) return null
              return (
                <NumberInput
                  label={`Commission (0-100) / default: ${
                    user?.defaultCommission
                  }%`}
                  source="foreignCommission"
                  fullWidth
                  validate={validateCommission}
                  variant="standard"
                  classes={{ root: 'number-input-without-arrows' }}
                />
              )
            }}
          </FormDataConsumer>
        </Grid>
        <Grid item xs={12} ml={2}>
          <PeriodCommissionsInput
            fullWidth
            variant="standard"
            label="Period commissions"
            source="foreignCommissions"
          />
        </Grid>
      </>
    )
  )
}

const InfoTab = ({ renderCoverImage, isCreating, ...props }) => {
  const validateDescription = [
    validateRequired('Description'),
    minLength(100),
    maxLength(2000, 'Do not enter more than 2000 characters'),
  ]

  return (
    <GridContainer fullWidth>
      {!isCreating && (
        <>
          <Grid container item xs={4}>
            <TextInput
              disabled
              fullWidth
              source="id"
              variant="standard"></TextInput>
          </Grid>
          <Grid container item xs={8}>
            <TextInput
              disabled
              fullWidth
              source="productId"
              variant="standard"></TextInput>
          </Grid>
        </>
      )}

      <Grid item xs={6}>
        <ReferenceInput
          label="Related Ebook"
          source="relatedEbookId"
          reference="ebooks"
          alwaysOn>
          <AutocompleteInput
            optionText="title"
            fullWidth
            variant="standard"
            filterToQuery={searchText => ({
              title: { $iLike: `%${searchText}%` },
            })}
          />
        </ReferenceInput>
      </Grid>

      <Grid item xs={6}>
        <ReferenceInput
          label="Related BookSummary"
          source="relatedBookSummaryId"
          reference="book-summaries">
          <AutocompleteInput
            optionText="title"
            fullWidth
            variant="standard"
            filterToQuery={searchText => ({
              title: { $iLike: `%${searchText}%` },
            })}
          />
        </ReferenceInput>
      </Grid>

      <Grid item xs={6}>
        <SelectInput
          label="Is free"
          source="isFree"
          choices={[
            { id: true, name: 'Yes' },
            { id: false, name: 'No' },
          ]}
          required
          defaultValue={false}
          fullWidth
          variant="standard"
          margin="normal"
        />
      </Grid>
      <Grid item xs={3}>
        <BooleanInput label="Hide Revenue" source="isHideRevenue" />
      </Grid>
      <Grid item xs={3}>
        <BooleanInput label="AI Book" source="isAiBook" />
      </Grid>
      <Grid item xs={6}>
        <SelectInput
          source="minAge"
          label="Age Allowed (optional)"
          choices={MIN_AGE_CHOICES}
          fullWidth
          variant="standard"
        />
      </Grid>
      <FormDataConsumer>
        {({ formData }) => {
          if (!formData || !formData.isFree) return null

          return (
            <Grid item xs={12}>
              <TextInput source="whyItFree" fullWidth variant="standard" />
            </Grid>
          )
        }}
      </FormDataConsumer>

      <FormDataConsumer>
        {({ formData }) => {
          if (!formData || formData.isFree) return null

          return (
            <Grid item xs={6}>
              <SelectInput
                label="Is membership included"
                source="membershipIncluded"
                choices={[
                  { id: true, name: 'Yes' },
                  { id: false, name: 'No' },
                ]}
                required
                defaultValue={false}
                fullWidth
                variant="standard"
                margin="normal"
              />
            </Grid>
          )
        }}
      </FormDataConsumer>
      <Grid item xs={6}>
        <SelectInput
          label="Is library included"
          source="isLibIncluded"
          choices={[
            { id: true, name: 'Yes' },
            { id: false, name: 'No' },
          ]}
          required
          defaultValue={false}
          fullWidth
          variant="standard"
          margin="normal"
        />
      </Grid>
      <Grid item xs={12}>
        <TextInput
          source="title"
          fullWidth
          validate={validateRequired('Title')}
          variant="standard"
        />
      </Grid>

      <Grid item xs={4}>
        <ReferenceInput
          label="Primary category"
          source="primaryCategoryId"
          reference="categories">
          <AutocompleteInput
            optionText="name"
            fullWidth
            required
            variant="standard"
            validate={validateRequired('Primary category')}
            filterToQuery={searchText => ({
              name: { $iLike: `%${searchText}%` },
              type: 'book',
              primary: true,
            })}
          />
        </ReferenceInput>
      </Grid>

      <Grid item xs={8}>
        <FormDataConsumer>
          {({ formData }) => {
            if (!formData.primaryCategoryId) return null
            return (
              <ReferenceArrayInput
                label="Secondary categories"
                source="secondaryCategoryIds"
                reference="categories">
                <AutocompleteArrayInput
                  optionText="name"
                  fullWidth
                  required
                  variant="standard"
                  filterToQuery={searchText => ({
                    name: { $iLike: `%${searchText}%` },
                    type: 'book',
                    primary: false,
                    parentId: formData.primaryCategoryId,
                  })}
                />
              </ReferenceArrayInput>
            )
          }}
        </FormDataConsumer>
      </Grid>

      <Grid item xs={6}>
        <ReferenceArrayInput
          label="Topics"
          source="topicIds"
          reference="topics">
          <AutocompleteArrayInput
            optionText="name"
            options={{
              fullWidth: true,
            }}
            variant="standard"
            filterToQuery={searchText => ({
              name: { $iLike: `%${searchText}%` },
              type: ['book', 'all'],
            })}
          />
        </ReferenceArrayInput>
      </Grid>

      <Grid item xs={6}>
        <ReferenceArrayInput
          label="Authors"
          source="authorIds"
          reference="authors">
          <AutocompleteArrayInput
            optionText="name"
            options={{
              fullWidth: true,
            }}
            variant="standard"
            validate={validateRequired('Author')}
            filterToQuery={searchText => ({
              name: { $iLike: `%${searchText}%` },
            })}
          />
        </ReferenceArrayInput>
      </Grid>

      <Grid item xs={6}>
        <ReferenceArrayInput
          label="Collection codes"
          source="collectionCodeIds"
          reference="collection-codes">
          <AutocompleteArrayInput
            optionText="name"
            options={{
              fullWidth: true,
            }}
            variant="standard"
            filterToQuery={searchText => ({
              name: { $iLike: `%${searchText}%` },
            })}
          />
        </ReferenceArrayInput>
      </Grid>

      <Grid item xs={4}>
        <ReferenceInput
          label="Publisher"
          source="publisherId"
          reference="users"
          perPage={1000}>
          <AutocompleteInput
            optionText="name"
            options={{
              fullWidth: true,
            }}
            variant="standard"
            validate={validateRequired('Publisher')}
            filterToQuery={searchText => ({
              name: { $iLike: `%${searchText}%` },
              role: ROLES.PUBLISHER,
            })}
          />
        </ReferenceInput>
      </Grid>

      <PublisherCommission />

      <Grid item xs={4}>
        <ReferenceInput
          label="Publisher nước ngoài (nếu có)"
          source="foreignPublisherId"
          reference="users"
          filter={{
            role: 'publisher',
          }}
          perPage={1000}>
          <SelectInput
            optionText="name"
            options={{
              fullWidth: true,
            }}
            variant="standard"
          />
        </ReferenceInput>
      </Grid>

      <ForeignPublisherCommission />

      <Grid item xs={12}>
        <SelectInput
          source="price"
          choices={PRICE_CHOICES}
          required
          fullWidth
          variant="standard"
          margin="normal"
          validate={validateRequired('Price')}
        />
      </Grid>

      <Grid item xs={6}>
        <TextInput
          label="Nhà xuất bản"
          source="responsiblePublisher"
          fullWidth
          variant="standard"
        />
      </Grid>

      <Grid item xs={6}>
        <TextInput
          label="Nhà xuất bản điện tử"
          source="digitalPublisher"
          fullWidth
          variant="standard"
        />
      </Grid>

      <Grid item xs={6}>
        <TextInput
          label="Nhà xuất bản Sách giấy"
          source="paperPublisher"
          fullWidth
          variant="standard"
        />
      </Grid>

      <Grid item xs={6}>
        <TextInput
          label="Chịu trách nhiệm xuất bản, nội dung"
          source="responsibleContent"
          fullWidth
          variant="standard"
        />
      </Grid>
      <Grid item xs={6}>
        <TextInput
          label="Chịu trách nhiệm bản thảo"
          source="manuscriptCurator"
          fullWidth
          variant="standard"
        />
      </Grid>

      <Grid item xs={6}>
        <TextInput
          label="Biên tập"
          source="editorAndPrintEditing"
          fullWidth
          variant="standard"
        />
      </Grid>

      <Grid item xs={6}>
        <TextInput
          source="publishDecisionNumber"
          label="Số quyết định xuất bản"
          fullWidth
          variant="standard"
        />
      </Grid>

      <Grid item xs={6}>
        <TextInput
          source="publishConfirmationNumber"
          label="Số xác nhận đăng ký xuất bản"
          fullWidth
          variant="standard"
        />
      </Grid>

      <Grid item xs={6}>
        <TextInput
          label="Số ISBN"
          source="isbnNumber"
          fullWidth
          variant="standard"
        />
      </Grid>

      <Grid item xs={6}>
        <TextInput
          source="depositary"
          label="Nộp lưu chiểu"
          fullWidth
          variant="standard"
        />
      </Grid>

      <Grid item xs={12}>
        <TextInput
          source="description"
          resettable
          rows={5}
          fullWidth
          variant="standard"
          validate={validateDescription}
          multiline
        />
      </Grid>

      <Grid item xs={12} ml={2}>
        <ArrayInput
          source="audiences"
          label="Who's it for"
          fullWidth
          variant="standard">
          <SimpleFormIterator>
            <TextInput
              fullWidth
              variant="standard"
              source="audience"
              label="Text"
              validate={validateRequired('Text')}
            />
          </SimpleFormIterator>
        </ArrayInput>
      </Grid>

      {/*<Grid item xs={12}>*/}
      {/*  <ReferenceArrayInput*/}
      {/*    label="Voice actors"*/}
      {/*    source="voiceActorIds"*/}
      {/*    reference="voice-actors">*/}
      {/*    <AutocompleteArrayInput*/}
      {/*      optionText="name"*/}
      {/*      fullWidth*/}
      {/*      variant="standard"*/}
      {/*      filterToQuery={searchText => ({*/}
      {/*        name: { $iLike: `%${searchText}%` },*/}
      {/*      })}*/}
      {/*    />*/}
      {/*  </ReferenceArrayInput>*/}
      {/*</Grid>*/}

      <Grid item xs={12}>
        <ImageInput
          source="coverImageUrl"
          label="Cover image"
          accept="image/*"
          multiple={false}
          validate={validateRequired('Cover Image')}>
          <CustomImageField source="src" title="Cover image" />
        </ImageInput>
      </Grid>
    </GridContainer>
  )
}

const VoiceActorManagementTab = ({ ...props }) => {
  const voiceActorIds = useWatch({ name: 'voiceActorIds' }) || []
  const { setValue, getValues } = useFormContext()
  const prevVoiceActorIdsRef = useRef([])
  const { data: voiceActors, isLoading } = useGetMany(
    'voice-actors',
    {
      ids: voiceActorIds,
    },
    { enabled: voiceActorIds.length > 0 },
  )

  const validateChapterOrder = [
    validateRequired('Order'),
    minValue(1),
    maxValue(1000),
  ]

  // Sync fileSamples when voiceActorIds changes
  useEffect(() => {
    // Check if voiceActorIds actually changed to prevent infinite loops
    const prevIds = prevVoiceActorIdsRef.current
    const idsChanged = JSON.stringify(prevIds) !== JSON.stringify(voiceActorIds)
    console.log(voiceActorIds)
    if (idsChanged) {
      prevVoiceActorIdsRef.current = voiceActorIds

      const currentFileSamples = getValues('fileSamples') || []

      if (voiceActorIds.length > 0) {
        // Multi-voice mode: create samples for each voice actor
        const newFileSamples = voiceActorIds.map((voiceId, index) => {
          const existing = currentFileSamples.find(
            sample => sample.voiceId === voiceId,
          )
          return existing || { voiceId, url: null }
        })

        // Only update if the arrays are actually different
        if (
          JSON.stringify(currentFileSamples) !== JSON.stringify(newFileSamples)
        ) {
          setValue('fileSamples', newFileSamples)
        }

        // Sync chapters voices
        const currentChapters = getValues('chapters') || []
        const updatedChapters = currentChapters.map(chapter => ({
          ...chapter,
          voices: voiceActorIds.map((voiceId, index) => {
            const existing = chapter.voices?.find(
              voice => voice.voiceId === voiceId,
            )
            return existing || { voiceId, url: null }
          }),
        }))

        // Only update if chapters actually changed
        if (
          JSON.stringify(currentChapters) !== JSON.stringify(updatedChapters)
        ) {
          setValue('chapters', updatedChapters)
        }
      } else {
        // Single-voice mode: ensure fileSamples has one entry with null voiceId
        const singleSample = currentFileSamples.find(
          sample => sample.voiceId === null,
        ) ||
          currentFileSamples[0] || { voiceId: null, url: null }

        const newFileSamples = [{ ...singleSample, voiceId: null }]

        // Only update if the arrays are actually different
        if (
          JSON.stringify(currentFileSamples) !== JSON.stringify(newFileSamples)
        ) {
          setValue('fileSamples', newFileSamples)
        }
      }
    }
  }, [voiceActorIds])

  if (isLoading) {
    return <div>Loading voice actors...</div>
  }

  // Convert voiceActorIds to voice actor objects
  const selectedVoiceActors = voiceActorIds.map((id, index) => {
    const voiceActor = voiceActors?.find(va => va.id === id)
    return voiceActor || { id, name: `Voice Actor ${index + 1}` }
  })

  return (
    <>
      {selectedVoiceActors.length > 0 && (
        <>
          <Grid item xs={12}>
            <h3>Samples</h3>
            <p
              style={{
                color: '#666',
                fontSize: '14px',
                marginBottom: '1rem',
              }}>
              Upload sample audio files for each voice actor
            </p>
          </Grid>

          <Grid item xs={12} ml={2}>
            <ArrayInput
              source="fileSamples"
              label="Files"
              required={true}
              fullWidth>
              <SimpleFormIterator disableAdd disableRemove>
                <Grid container spacing={2} style={{ width: '100%' }}>
                  <Grid item xs={12} md={4}>
                    <ReferenceInput
                      source="voiceId"
                      reference="voice-actors"
                      label="Voice Actor">
                      <SelectInput
                        optionText="name"
                        variant="standard"
                        fullWidth
                        disabled={false}
                        style={{
                          '& .MuiInputBase-input.Mui-disabled': {
                            WebkitTextFillColor: '#000',
                            fontWeight: 'bold',
                          },
                        }}
                      />
                    </ReferenceInput>
                  </Grid>
                  <Grid item xs={12} md={8}>
                    <CustomFileInput
                      source="url"
                      label="FileSample"
                      type="fileSample"
                      upload={true}
                      showProgress
                      validate={validateRequired('File Sample')}>
                      <CustomFileField
                        source="src"
                        title="File Sample"
                        variant="standard"
                      />
                    </CustomFileInput>

                    {/* Audio Player for Sample File Review */}
                    <Box sx={{ mt: 2 }}>
                      <CustomAudioPlayerField
                        showInfo={false}
                        source="url"
                        label="Sample File Audio Player"
                        variant="standard"
                      />
                    </Box>
                  </Grid>
                </Grid>
              </SimpleFormIterator>
            </ArrayInput>
          </Grid>

          {selectedVoiceActors.length > 0 && (
            <>
              <Grid item xs={12}>
                <h3>Chapters</h3>
                <p
                  style={{
                    color: '#666',
                    fontSize: '14px',
                    marginBottom: '1rem',
                  }}>
                  Manage chapters and upload audio files for each voice actor
                </p>
              </Grid>
              <Grid item xs={12}>
                <ChaptersManagerInput voiceActors={selectedVoiceActors} />
              </Grid>
            </>
          )}
        </>
      )}
    </>
  )
}

const SingleVoiceContentTab = ({ ...props }) => {
  const { setValue, getValues } = useFormContext()
  const voiceActorIds = useWatch({ name: 'voiceActorIds' }) || []
  const { data: voiceActors } = useGetMany(
    'voice-actors',
    { ids: voiceActorIds },
    { enabled: voiceActorIds.length > 0 },
  )
  const singleVoiceActors = voiceActorIds.slice(0, 1).map(id => {
    const va = voiceActors?.find(v => v.id === id)
    return va || { id, name: `Voice Actor` }
  })

  // Sync fileSamples structure to match VoiceActorManagementTab
  useEffect(() => {
    const currentFileSamples = getValues('fileSamples') || []

    if (voiceActorIds.length > 0) {
      // Single voice mode but with voice structure: use first voiceActorId
      const voiceId = voiceActorIds[0]
      const existingUrl =
        currentFileSamples[0]?.url ||
        currentFileSamples[0]?.voices?.[0]?.url ||
        null

      const newFileSamples = [
        {
          voiceId,
          url: existingUrl,
        },
      ]

      if (
        JSON.stringify(currentFileSamples) !== JSON.stringify(newFileSamples)
      ) {
        setValue('fileSamples', newFileSamples)
      }
    } else {
      // Fallback: single sample with null voiceId
      const existingUrl = currentFileSamples[0]?.url || null
      const newFileSamples = [
        {
          voiceId: null,
          url: existingUrl,
        },
      ]

      if (
        JSON.stringify(currentFileSamples) !== JSON.stringify(newFileSamples)
      ) {
        setValue('fileSamples', newFileSamples)
      }
    }

    // Sync chapters voices structure to match VoiceActorManagementTab
    const currentChapters = getValues('chapters') || []
    const updatedChapters = currentChapters.map(chapter => {
      if (voiceActorIds.length > 0) {
        // Single voice mode: create voices array with first voiceActorId
        const voiceId = voiceActorIds[0]
        const existingUrl = chapter.url || chapter.voices?.[0]?.url || null

        return {
          ...chapter,
          voices: [{ voiceId, url: existingUrl }],
        }
      } else {
        // Fallback: single voice with null voiceId
        const existingUrl = chapter.url || null
        return {
          ...chapter,
          voices: [{ voiceId: null, url: existingUrl }],
        }
      }
    })

    // Only update if chapters actually changed
    if (JSON.stringify(currentChapters) !== JSON.stringify(updatedChapters)) {
      setValue('chapters', updatedChapters)
    }
  }, [voiceActorIds, setValue, getValues])

  return (
    <>
      <Grid item xs={12}>
        <h3>Samples</h3>
      </Grid>

      <Grid item xs={12}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <CustomFileInput
              source="fileSamples[0].url"
              label="Sample Audio File"
              type="fileSample"
              upload={true}
              showProgress
              validate={validateRequired('Sample file')}>
              <CustomFileField
                source="src"
                title="Sample File"
                variant="standard"
              />
            </CustomFileInput>

            {/* Audio Player for Sample File Review */}
            <Box sx={{ mt: 2 }}>
              <FormDataConsumer>
                {({ formData }) => {
                  const sampleUrl = formData?.fileSamples?.[0]?.url
                  return sampleUrl ? (
                    <CustomAudioPlayerField
                      audioPath={sampleUrl}
                      label="Sample Audio Player"
                      showInfo={false}
                    />
                  ) : null
                }}
              </FormDataConsumer>
            </Box>
          </Grid>
        </Grid>
      </Grid>

      <Grid item xs={12}>
        <h3>Chapters</h3>
      </Grid>
      <Grid item xs={12}>
        <ChaptersManagerInput voiceActors={singleVoiceActors} />
      </Grid>
    </>
  )
}

const ContentTab = ({ renderSampleFile, ...props }) => {
  return (
    <GridContainer>
      <Grid item xs={12} md={4}>
        <BooleanInput
          source="multiVoices"
          label="Multi Voices"
          defaultValue={false}
          variant="standard"
        />
      </Grid>

      <Grid item xs={12}>
        <ReferenceArrayInput
          label="Voice actors"
          source="voiceActorIds"
          reference="voice-actors">
          <AutocompleteArrayInput
            optionText="name"
            fullWidth
            variant="standard"
            filterToQuery={searchText => ({
              name: { $iLike: `%${searchText}%` },
            })}
          />
        </ReferenceArrayInput>
      </Grid>

      <FormDataConsumer>
        {({ formData }) => {
          const isMulti = formData?.multiVoices !== false
          return isMulti ? (
            <VoiceActorManagementTab
              renderSampleFile={renderSampleFile}
              {...props}
            />
          ) : (
            <SingleVoiceContentTab {...props} />
          )
        }}
      </FormDataConsumer>
    </GridContainer>
  )
}

const BookForm = props => {
  const [tabValue, setTabValue] = useState(0)

  const handleTabChange = (_event, newValue) => {
    setTabValue(newValue)
  }

  const renderCoverImage = record => (
    <CustomImageField
      source="coverImageUrl"
      record={record}
      title="Cover Image"
    />
  )

  const isCreating = !props.record?.id

  return (
    <SimpleForm toolbar={<BookToolbar />} {...props}>
      <GridContainer>
        <Box sx={{ width: '100%' }}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs value={tabValue} onChange={handleTabChange}>
              <Tab label="Information" />
              <Tab label="Contents" />
            </Tabs>
          </Box>
          {tabValue === 0 && (
            <InfoTab
              renderCoverImage={renderCoverImage}
              isCreating={isCreating}
              {...props}
            />
          )}
          {tabValue === 1 && (
            <Box sx={{ paddingTop: 2 }}>
              <ContentTab {...props} />
            </Box>
          )}
        </Box>
      </GridContainer>
    </SimpleForm>
  )
}

export default BookForm
